import cn from 'classnames';
import { format as formatDate, parseJSON } from 'date-fns';
import type { ReactNode } from 'react';
import { formatLongDateTime } from '#util/time.ts';
import styles from './Timestamp.module.css';

const DEFAULT_FORMAT = 'M/d/yyyy ppp';

type Props = {
  /** RFC3339; fractional seconds accepted */
  datetime: string;
  className?: string;
} & (
  | {
      /** date-fns format for the display text, see https://date-fns.org/docs/format */
      format?: string;
      children?: never;
    }
  | {
      /** Display text that isn't a plain absolute format — a relative distance, "Online", … */
      children: ReactNode;
      format?: never;
    }
);

/**
 * The `<time>` element for any instant the API hands us: machine-readable `dateTime`, a display
 * value (an absolute format by default, or whatever `children` say), and the instant spelled out
 * in the viewer's locale and zone in the title, so hovering answers "when, exactly?".
 */
export function Timestamp({ datetime, className, format = DEFAULT_FORMAT, children }: Props) {
  const parsed = parseJSON(datetime);
  return (
    <time className={cn(className, styles.Timestamp)} dateTime={datetime} title={formatLongDateTime(parsed)}>
      {children === undefined ? formatDate(parsed, format) : children}
    </time>
  );
}
